import * as React from 'react';
import isFunction from 'lodash/isFunction';
import { connect } from 'react-redux';

import PromptExit from 'libs/router/prompt-exit/PromptExit';
import {
  CcmCreatorlessCampaignTimelineEstimation
} from 'modules/campaigns/components/campaign-timeline/components/creatorless';
import ActionButton from 'components/buttons/action-button/ActionButton';
import { Form, FormActions } from 'components/form';
import Name from './components/Name';
import Package from './components/Package';
import GoLiveDate from './components/GoLiveDate';
import Countries from './components/Countries';
import TargetGroup from './components/TargetGroup';
import ZalandoLink from './components/ZalandoLink';

import { fetchCampaignBrief } from 'modules/campaigns/reducers/campaignBrief';
import {
  createCcmCreatorlessCampaign,
  flush,
  init,
  updateCcmCreatorlessCampaign
} from '../redux/actions';

import { navigate } from 'libs/router';
import { __t, applyTranslations } from 'utils/language';
import { hasChanges } from '../utils';

import './ccm-campaign-form-creatorless.scss';

applyTranslations(require('./ccm-campaign-form-creatorless.i18n.json'));

import type { Campaign } from 'types';
import type {
  CcmCreatorlessCampaignTimeline
} from 'modules/campaigns/components/ccm/types/CcmCampaignTimeline.type';
import type {
  CreatorlessCampaign,
  CcmCampaignFormCreatorlessThunkDispatch as Dispatch,
  StateAsPartOfGlobal,
  SubmittionState
} from '../types';

type OwnProps = {
  campaign: null | Campaign,
  pageHeading: JSX.Element,
  organizationId: number,
  brandProfileId: number,
  onCloseModal?: () => void
};

type StateProps = {
  form: CreatorlessCampaign,
  timeline: null | CcmCreatorlessCampaignTimeline,
  submittionState: SubmittionState
};

type DispatchProps = {
  onMount: (c: null | Campaign) => void,
  onUnmount: () => void,
  onCreate: (
    brandProfileId: number,
    c: CreatorlessCampaign,
    cb: (campaignId: number) => void
  ) => void,
  onUpdate: (
    campaignId: number,
    c: CreatorlessCampaign,
    cb: (campaignId: number) => void
  ) => void
};

type Props = OwnProps & StateProps & DispatchProps;

const COMMON_MSG_KEY = 'collabary_common';
const MSG_KEY = 'ccm_campaign_form_creatorless';

const CcmCampaignFormCreatorless = ({
  campaign,
  timeline,
  pageHeading,
  organizationId,
  brandProfileId,
  form,
  submittionState,
  onMount,
  onUnmount,
  onCreate,
  onUpdate,
  onCloseModal
}: Props): JSX.Element => {
  React.useEffect(() => {
    onMount(campaign);

    return onUnmount;
  }, []);

  return (
    <div className="ccm-campaign-form-creatorless">
      <Form
        onSubmit={() => {
          if (campaign === null) {
            onCreate(
              brandProfileId,
              form,
              campaignId => setTimeout(() => {
                navigate.to(`/campaigns/ccm/${campaignId}/overview?type=ccm_creatorless`);
              }, 300)
            );
          } else {
            onUpdate(campaign.id, form, onCloseModal ?? (() => undefined));
          }
        }}
      >
        {hasChanges(form) ? (
          <PromptExit type="page" />
        ) : null}
        {pageHeading}

        <div className="ccm-campaign-form-creatorless__sections">
          <div className="ccm-campaign-form-creatorless__section">
            <div className="ccm-campaign-form-creatorless__form">
              <Name
                className="ccm-campaign-form-creatorless__name"
                mgsKey={MSG_KEY}
              />
              <Package
                disabled={campaign !== null}
                className="ccm-campaign-form-creatorless__package"
                mgsKey={MSG_KEY}
              />
              <GoLiveDate
                initialDate={campaign?.timeline?.taskContentGoLiveDate ?? null}
                classNameBase="ccm-campaign-form-creatorless"
                mgsKey={MSG_KEY}
                disabled={(campaign?.participantsCount ?? 0) > 0}
              />
              {timeline ? (
                <CcmCreatorlessCampaignTimelineEstimation
                  timeline={timeline}
                />
              ) : null}
              <Countries
                className="ccm-campaign-form-creatorless__countries"
                mgsKey={MSG_KEY}
              />
              <TargetGroup
                mgsKey={MSG_KEY}
                classNameBase="ccm-campaign-form-creatorless"
              />
              <ZalandoLink
                classNameBase="ccm-campaign-form-creatorless"
                mgsKey={MSG_KEY}
              />
            </div>
          </div>
          {submittionState.type === 'error' ? (
            <div className="ccm-campaign-form-creatorless__submittion-error">
              {submittionState.message}
            </div>
          ) : null}
          <div className="ccm-campaign-form-creatorless__section">
            <div className="ccm-campaign-form-creatorless__form">
              <FormActions
                align="center"
                containerClass="ccm-campaign-form-creatorfull__form-actions"
              >
                <ActionButton
                  color="tertiary"
                  onClick={() => {
                    if (campaign !== null && isFunction(onCloseModal)) {
                      onCloseModal();
                      return;
                    }

                    navigate.to(`/organizations/${organizationId}/campaigns`);
                  }}
                >
                  {__t(`${COMMON_MSG_KEY}.action_button.cancel`)}
                </ActionButton>
                <ActionButton
                  color="primary"
                  type="submit"
                >
                  {campaign === null ? (
                    __t(`${COMMON_MSG_KEY}.action_button.submit`)
                  ) : (
                    __t(`${COMMON_MSG_KEY}.action_button.edit`)
                  )}
                </ActionButton>
              </FormActions>
            </div>
          </div>
        </div>
      </Form>
    </div>
  );
};

const mapStateToProps = ({ ccmCampaignFormCreatorless }: StateAsPartOfGlobal): StateProps => ({
  form: ccmCampaignFormCreatorless.form,
  timeline: ccmCampaignFormCreatorless.timeline,
  submittionState: ccmCampaignFormCreatorless.submittionState
});

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
  onMount: campaign => dispatch(init(campaign)),
  onUnmount: () => dispatch(flush()),
  onCreate: (brandProfileId, campaign, cb) =>
    dispatch(createCcmCreatorlessCampaign(brandProfileId, campaign, cb)),
  onUpdate: (campaignId, campaign, cb) =>
    dispatch(updateCcmCreatorlessCampaign(campaignId, campaign, cmpgnId => {
      dispatch(fetchCampaignBrief(cmpgnId, false));
      cb(cmpgnId);
    }))
});

export default connect(mapStateToProps, mapDispatchToProps)(CcmCampaignFormCreatorless);
